import { useState } from "react";
import "../../css/GlobalStyles.css";
import PopupModalContainer, {
  PopupModalContainerWidth,
} from "../Containers/PopupModalContainer";
import {
  AccountData,
  Event,
  Follower,
  SavedFormQuestion,
  SavedFormResponse,
} from "@markit/common.types";
import { useTheme } from "../../hooks/useTheme";
import SingleCustomDataItem from "./SingleCustomDataItem";
import { GetDateWithTime, isExternalGenericLink } from "@markit/common.utils";
import { HorizontalDivider } from "../Dividers/HorizontalDivider";
import RectangleButton from "../Buttons/RectangleButton";
import { Colors } from "../../utils/colors";
import { fetchSingleEvent } from "../../utils/FetchSingleData";
import { useSelector } from "react-redux";
import { getEventState } from "../../redux/slices/eventSlice";
import useAsyncEffect from "../../hooks/useAsyncEffect";
import SingleEditCustomData from "./SingleEditCustomData";

interface SingleCustomDataProps {
  savedQuestion: SavedFormQuestion;
  formResponse: SavedFormResponse | undefined;
  setFormResponse: (formResponse: SavedFormResponse) => void;
  userData: AccountData;
  followerData: Follower | undefined;
  closeSingleCustomData: () => void;
}

const SingleCustomData = (props: SingleCustomDataProps) => {
  const {
    savedQuestion,
    formResponse,
    setFormResponse,
    userData,
    followerData,
    closeSingleCustomData,
  } = props;
  const { theme } = useTheme();
  const { events } = useSelector(getEventState).events;
  const [sourceEvent, setSourceEvent] = useState<Event>();
  const [editDetailsModal, setEditDetailsModal] = useState(false);

  const styles = {
    bodyText: {
      color: formResponse ? theme.PrimaryText.color : theme.SubText.color,
      fontSize: 14,
      fontWeight: 500,
    },

    sectionContainer: {
      paddingBlock: 14,
      borderRadius: 12,
      border: `0.5px solid ${Colors.GRAY2}`,
    },

    subSectionContainer: {
      paddingInline: 14,
      gap: 4,
    },
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useAsyncEffect(async () => {
    if (formResponse && formResponse.eventId) {
      const event = await fetchSingleEvent(formResponse.eventId, events);
      setSourceEvent(event);
    }
  }, [savedQuestion, formResponse, events]);

  if (editDetailsModal) {
    return (
      <SingleEditCustomData
        savedQuestion={savedQuestion}
        savedFormResponse={formResponse}
        setSavedFormResponse={setFormResponse}
        userId={userData.uid}
        eventId=""
        setModalVisible={setEditDetailsModal}
      />
    );
  }

  return (
    <PopupModalContainer
      headerComp={<span className="sectionTitle">Details</span>}
      valueComp={
        <div
          style={{
            ...styles.sectionContainer,
            backgroundColor: theme.NewPrimaryBG.backgroundColor,
          }}
        >
          {savedQuestion ? (
            <SingleCustomDataItem
              savedQuestion={savedQuestion}
              formResponse={formResponse}
              userData={userData}
              followerData={followerData}
              detailView
            />
          ) : null}
          <div className="ColumnNormal" style={styles.subSectionContainer}>
            <span className="bodySubtext">Last Updated</span>
            <span style={styles.bodyText}>
              {formResponse
                ? GetDateWithTime(formResponse.lastResponded, true)
                : "N/A"}
            </span>
          </div>
          <HorizontalDivider altMargin={14} />
          <div className="ColumnNormal" style={styles.subSectionContainer}>
            <span className="bodySubtext">Source</span>
            <span style={styles.bodyText}>
              {formResponse
                ? formResponse.isEdited
                  ? "Edited Manually"
                  : sourceEvent
                    ? `${
                        isExternalGenericLink(sourceEvent.eventType)
                          ? "Submitted to Form:"
                          : "Went to Event:"
                      } ${sourceEvent.title}`
                    : "Followed Profile"
                : "N/A"}
            </span>
          </div>
        </div>
      }
      noExit
      closeOnOutsidePress
      modalContentWidth={PopupModalContainerWidth.SMALL}
      fixedBottomContent={
        <div style={{ paddingInline: 14, paddingTop: 14 }}>
          <RectangleButton
            buttonLabel="Edit"
            onPress={() => setEditDetailsModal(true)}
            altColor={Colors.WHITE1}
            altTextColor={Colors.BLACK}
            containerStyles={{
              paddingInline: 12,
              paddingBlock: 14,
              border: `0.5px solid ${Colors.GRAY2}`,
            }}
          />
        </div>
      }
      closeModal={closeSingleCustomData}
    />
  );
};

export default SingleCustomData;
